const SAO_PAULO_TZ = "America/Sao_Paulo";

export const formatAssinaturaDateTime = (value, { seconds = false } = {}) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    timeZone: SAO_PAULO_TZ,
  });
};

export const formatAssinaturaAccessDateTime = (value) => formatAssinaturaDateTime(value, { seconds: true });

const formatDateTime = formatAssinaturaDateTime;

const toTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

export const isFinalizacaoLinkEfetivamenteEnviado = (assinatura, context = {}) => {
  if (!assinatura || assinatura.tipo !== "finalizacao") {
    return Boolean(assinatura?.enviado_em);
  }
  if (assinatura.status === "assinado") return true;
  if (context.kmFinalDisparadoEm) return true;

  const motStart = context.motoristaInicioEm;
  if (!assinatura.enviado_em || !motStart) return false;
  return toTimestamp(assinatura.enviado_em) >= toTimestamp(motStart);
};

export const getFinalizacaoAgendamentoDisplay = (context = {}) => {
  if (context.kmFinalDisparadoEm) return null;
  if (context.kmFinalDispararEm) {
    const quando = formatDateTime(context.kmFinalDispararEm);
    return quando
      ? `Envio do link previsto para ${quando}`
      : "Envio do link agendado para o fim da rota";
  }
  if (context.motoristaInicioEm) {
    return "Link será enviado ao final da rota";
  }
  return "Aguardando motorista iniciar a rota";
};

export const buildLembreteAuditoriaFromEventos = (eventos = [], {
  documentoId = null,
  eventoTipos = [],
  assinatura = null,
} = {}) => {
  const allowed = new Set(eventoTipos);
  const assinaturaId = Number(assinatura?.id) || null;
  const funcionarioId = Number(assinatura?.funcionario_id) || null;
  const assinaturaStatus = String(assinatura?.status || '').toLowerCase();

  const wasLembreteDestinadoAssinatura = (event) => {
    if (!assinatura) return true;

    const lembretes = event?.detalhes?.lembretes;
    if (Array.isArray(lembretes) && lembretes.length) {
      return lembretes.some((item) => (
        (assinaturaId && Number(item.assinatura_id) === assinaturaId)
        || (funcionarioId && Number(item.funcionario_id) === funcionarioId)
      ));
    }

    // Eventos legados eram por documento — não atribuir a quem já respondeu.
    return !['assinado', 'recusado'].includes(assinaturaStatus);
  };

  return (Array.isArray(eventos) ? eventos : [])
    .filter((event) => {
      if (!allowed.has(event?.tipo)) return false;
      if (documentoId != null && Number(event?.detalhes?.documento_id) !== Number(documentoId)) {
        return false;
      }
      return wasLembreteDestinadoAssinatura(event);
    })
    .map((event) => {
      const tentativa = Number(event?.detalhes?.tentativa || 0) || null;
      const labelByTipo = {
        lembrete_auto_funcionario_inicio_enviado: tentativa
          ? `Lembrete automático #${tentativa} de assinatura enviado`
          : 'Lembrete automático de assinatura enviado',
        lembrete_auto_km_final_enviado: tentativa
          ? `Lembrete automático #${tentativa} de KM final enviado`
          : 'Lembrete automático de KM final enviado',
      };
      return {
        key: `lembrete-${event.id || event.tipo}-${tentativa || 'x'}`,
        icon: '🔔',
        label: labelByTipo[event.tipo] || event.mensagem || event.tipo,
        em: event.criado_em,
      };
    })
    .sort((a, b) => toTimestamp(a.em) - toTimestamp(b.em));
};

export const buildAssinaturaAuditoria = (assinatura, context = {}) => {
  const tipo = assinatura?.tipo || "funcionario";
  const events = [];
  const linkEnviado = isFinalizacaoLinkEfetivamenteEnviado(assinatura, context);
  const lembreteTipos = tipo === 'finalizacao'
    ? ['lembrete_auto_km_final_enviado']
    : tipo === 'funcionario'
      ? ['lembrete_auto_funcionario_inicio_enviado']
      : [];

  if (context.documentoEventos?.length && lembreteTipos.length) {
    events.push(...buildLembreteAuditoriaFromEventos(context.documentoEventos, {
      documentoId: context.documentoId,
      eventoTipos: lembreteTipos,
      assinatura,
    }));
  }

  if (assinatura?.termos_aceitos_em) {
    events.push({
      key: 'termos',
      icon: '📋',
      label: `Termos de uso dos dados aceitos${assinatura.termos_versao ? ` (v${assinatura.termos_versao})` : ''}`,
      em: assinatura.termos_aceitos_em,
    });
  }

  if (assinatura?.envio_falha_em) {
    events.push({
      key: 'envio_falha',
      icon: '⚠️',
      label: 'Falha ao enviar link no WhatsApp',
      em: assinatura.envio_falha_em,
      detail: assinatura.envio_falha_motivo || null,
    });
  } else if (linkEnviado && assinatura?.enviado_em) {
    events.push({
      key: "enviado",
      icon: "📤",
      label: tipo === "finalizacao" ? "Recebeu o link de finalização no WhatsApp" : "Recebeu o link no WhatsApp",
      em: assinatura.enviado_em,
    });
  }

  if (assinatura?.primeiro_acesso_em) {
    events.push({
      key: "aberto",
      icon: "👁️",
      label: "Primeira abertura do link",
      em: assinatura.primeiro_acesso_em,
      accessTime: true,
    });
  }

  const primeiroAcesso = toTimestamp(assinatura?.primeiro_acesso_em);
  const ultimoAcesso = toTimestamp(assinatura?.ultimo_acesso_em);
  if (ultimoAcesso && primeiroAcesso && ultimoAcesso - primeiroAcesso > 60_000) {
    events.push({
      key: "reaberto",
      icon: "👁️",
      label: "Voltou ao link",
      em: assinatura.ultimo_acesso_em,
      accessTime: true,
    });
  }

  if (assinatura?.status === "recusado" && assinatura?.recusado_em) {
    events.push({
      key: "recusado",
      icon: "🚫",
      label: "Recusou a viagem",
      em: assinatura.recusado_em,
      detail: assinatura.observacao_recusa || null,
    });
  } else if (assinatura?.assinado_em || assinatura?.horario_inicio_rota) {
    const em = assinatura?.horario_inicio_rota || assinatura?.assinado_em;
    const label = tipo === "motorista"
      ? "Iniciou a rota"
      : tipo === "finalizacao"
        ? "Finalizou a rota"
        : "Assinou";
    events.push({
      key: "assinado",
      icon: "✅",
      label,
      em,
    });
  }

  return events.sort((a, b) => toTimestamp(a.em) - toTimestamp(b.em));
};

const pendingLabel = (tipo) => {
  if (tipo === "motorista") return "Aguardando início da rota";
  if (tipo === "finalizacao") return "Aguardando confirmação de finalização";
  return "Aguardando assinatura";
};

export const getAssinaturaStatusDisplay = (assinatura, context = {}) => {
  const tipo = assinatura?.tipo || "funcionario";
  const status = assinatura?.status || "pendente";
  const pending = pendingLabel(tipo);
  const linkEnviado = isFinalizacaoLinkEfetivamenteEnviado(assinatura, context);

  // Visão programática (pedido): passageiro do pedido cujo link de assinatura ainda não foi enviado.
  if (assinatura?.nao_despachado) {
    return { text: "Aguardando envio do link de assinatura", color: "#a1a1aa", icon: "🕒" };
  }

  if (assinatura?.envio_falha_em) {
    const quando = formatDateTime(assinatura.envio_falha_em);
    const motivo = String(assinatura.envio_falha_motivo || '').trim();
    return {
      text: motivo
        ? `Falha no envio do link${quando ? ` em ${quando}` : ''} — ${motivo}`
        : quando
          ? `Falha no envio do link em ${quando}`
          : 'Falha no envio do link no WhatsApp',
      color: '#ef4444',
      icon: '⚠️',
    };
  }

  if (status === "assinado") {
    const quando = formatDateTime(assinatura?.assinado_em);
    if (tipo === "motorista") {
      return { text: quando ? `Início da rota em ${quando}` : "Rota iniciada", color: "#22c55e", icon: "✅" };
    }
    if (tipo === "finalizacao") {
      return { text: quando ? `Finalizou em ${quando}` : "Rota finalizada", color: "#22c55e", icon: "✅" };
    }
    return { text: quando ? `Assinou em ${quando}` : "Assinado", color: "#22c55e", icon: "✅" };
  }

  if (status === "recusado") {
    const quando = formatDateTime(assinatura?.recusado_em);
    return {
      text: quando ? `Respondeu · Recusou em ${quando}` : "Respondeu · Recusou a viagem",
      color: "#eab308",
      icon: "🚫",
    };
  }

  if (status === "visualizado" || assinatura?.primeiro_acesso_em) {
    const primeira = formatAssinaturaAccessDateTime(assinatura?.primeiro_acesso_em);
    const ultima = formatAssinaturaAccessDateTime(assinatura?.ultimo_acesso_em);
    const primeiroTs = toTimestamp(assinatura?.primeiro_acesso_em);
    const ultimoTs = toTimestamp(assinatura?.ultimo_acesso_em);
    const voltou = ultimoTs && primeiroTs && ultimoTs - primeiroTs > 60_000;

    let text;
    if (primeira && voltou && ultima) {
      text = `Primeira abertura em ${primeira} · voltou em ${ultima} — ${pending.toLowerCase()}`;
    } else if (primeira) {
      text = `Primeira abertura em ${primeira} — ${pending.toLowerCase()}`;
    } else {
      text = `Link aberto — ${pending.toLowerCase()}`;
    }

    return {
      text,
      color: "#38bdf8",
      icon: "👁️",
    };
  }

  if (tipo === "finalizacao" && !linkEnviado) {
    const agendamento = getFinalizacaoAgendamentoDisplay(context);
    return {
      text: agendamento || pending,
      color: "#a1a1aa",
      icon: "⏳",
    };
  }

  if ((status === "enviado" || assinatura?.enviado_em) && linkEnviado) {
    const quando = formatDateTime(assinatura?.enviado_em);
    const label = tipo === "finalizacao"
      ? "Link de finalização enviado no WhatsApp"
      : "Link enviado no WhatsApp";
    return {
      text: quando ? `${label} em ${quando}` : label,
      color: "#fbbf24",
      icon: "📤",
    };
  }

  return {
    text: tipo === "funcionario" ? "Aguardando envio do link" : pending,
    color: "#a1a1aa",
    icon: "⏳",
  };
};

export const getAssinaturaBorderColor = (assinatura) => {
  const status = assinatura?.status || "pendente";
  if (assinatura?.nao_despachado) return "rgba(148,163,184,0.28)";
  if (assinatura?.envio_falha_em) return "rgba(239,68,68,0.18)";
  if (status === "assinado") return "rgba(34,197,94,0.2)";
  if (status === "recusado") return "rgba(234,179,8,0.24)";
  if (status === "visualizado" || assinatura?.primeiro_acesso_em) return "rgba(56,189,248,0.25)";
  if (status === "enviado" || assinatura?.enviado_em) return "rgba(251,191,36,0.22)";
  return "var(--border)";
};
